import { useState, useCallback, useEffect, useContext, useRef } from "react";
import useRouteParam from "../../../../../../hooks/useRouteParam";
import { UIContext } from "../../../../../../contexts";
import OpenEnrollmentListsGrid from "./openEnrollmentListsGrid/OpenEnrollmentListsGrid";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../../constants/caption/dashboardHeader";
import { trackPromise } from "react-promise-tracker";
import { api } from "../../../../../../utils/api";
import { FormControl, Grid, InputLabel, MenuItem, Select, Switch } from "@material-ui/core";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import { withStyles } from "@material-ui/styles";

import "./openEnrollmentLists.css";
import { ENROLLER_TYPE_SWITCHER } from "../../../../../../constants/enrollmentSwitcher/EnrollmentSwitcher";
import { IOption } from "../../../../../../@types/Option.types";
import { Member } from "../../../../../../@types/member.types";
import { LazyEnrollments } from "../../../..";

const GreenSwitch = withStyles({
	switchBase: {
		color: "#359a4c",
		"&$checked": {
			color: "#359a4c"
		},
		"&$checked + $track": {
			backgroundColor: "rgba(40, 167, 69, 0.8)"
		}
	},
	checked: {},
	track: {
		backgroundColor: "rgb(60, 179, 113)"
	}
})(Switch);

const OpenEnrollmentLists = ({ employee }: { employee: Member }) => {
	const [currentOpenEnrollmentLists, setCurrentOpenEnrollmentLists] = useState<any[]>([]);
	const [futureOpenEnrollmentLists, setFutureOpenEnrollmentLists] = useState<any[]>([]);
	const [planCheckedStatus, setPlanCheckedStatus] = useState<boolean>(false);
	const [typeState, setTypeState] = useState(ENROLLER_TYPE_SWITCHER[1].value);
	const { setDashboardHeader } = useContext(UIContext);
	const queryGroupNumber = useRouteParam("gn");

	const assignOpenEnrollments = useCallback(
		(openEnrollments: any) => {
			const _currentOE: any[] = [],
				_futureOE: any[] = [];

			if (planCheckedStatus === false) {
				const { currentOpenEnrollments, futureOpenEnrollments } = openEnrollments;
				currentOpenEnrollments.forEach((currentOpenEnrollment: any) => {
					const { action, employee_SSN, open_enrollments, plans, group_owners } = currentOpenEnrollment;

					_currentOE.push({
						group_number: open_enrollments.group_number,
						plan_code: plans.plan_code,
						plan_name: plans.plan_name,
						open_enrollment_for: open_enrollments.open_enrollment_for,
						type: open_enrollments.type,
						start_date: open_enrollments.start_date,
						end_date: open_enrollments.end_date,
						plan_opened_on: open_enrollments.created_date,
						group_owners
					});
				});

				futureOpenEnrollments.forEach((currentOpenEnrollment: any) => {
					const { action, employee_SSN, open_enrollments, plans, group_owners } = currentOpenEnrollment;

					_futureOE.push({
						group_number: open_enrollments.group_number,
						plan_code: plans.plan_code,
						plan_name: plans.plan_name,
						open_enrollment_for: open_enrollments.open_enrollment_for,
						type: open_enrollments.type,
						start_date: open_enrollments.start_date,
						end_date: open_enrollments.end_date,
						plan_opened_on: open_enrollments.created_date,
						group_owners
					});
				});
			} else {
				const { currentVEBOpenEnrollments, futureVEBOpenEnrollments } = openEnrollments;
				currentVEBOpenEnrollments.forEach((currentOpenEnrollment: any) => {
					const { action, employee_SSN, veb_open_enrollments, veb_plans, group_owners } =
						currentOpenEnrollment;

					_currentOE.push({
						group_number: veb_open_enrollments.group_number,
						plan_code: veb_plans.plan_code,
						plan_name: veb_plans.plan_name,
						open_enrollment_for: veb_open_enrollments.open_enrollment_for,
						type: veb_open_enrollments.type,
						start_date: veb_open_enrollments.start_date,
						end_date: veb_open_enrollments.end_date,
						plan_opened_on: veb_open_enrollments.created_date,
						group_owners
					});
				});

				futureVEBOpenEnrollments.forEach((currentOpenEnrollment: any) => {
					const { action, employee_SSN, veb_open_enrollments, veb_plans, group_owners } =
						currentOpenEnrollment;

					_futureOE.push({
						group_number: veb_open_enrollments.group_number,
						plan_code: veb_plans.plan_code,
						plan_name: veb_plans.plan_name,
						open_enrollment_for: veb_open_enrollments.open_enrollment_for,
						type: veb_open_enrollments.type,
						start_date: veb_open_enrollments.start_date,
						end_date: veb_open_enrollments.end_date,
						plan_opened_on: veb_open_enrollments.created_date,
						group_owners
					});
				});
			}

			setCurrentOpenEnrollmentLists(Object.assign([], _currentOE));
			setFutureOpenEnrollmentLists(Object.assign([], _futureOE));
		},
		[planCheckedStatus]
	);

	const getOpenEnrollmentLists = useCallback(
		async (planType: "CORE" | "VEB") => {
			if (queryGroupNumber && employee) {
				const getOpenEnrollmentListPayload = {
					group_number: queryGroupNumber as number,
					employee_SSN: employee.SSN as string
				};
				const response =
					planType === "CORE"
						? await trackPromise(
								api.openEnrollment.getOpenEnrollmentsForEachEmployee(getOpenEnrollmentListPayload)
						  )
						: planType === "VEB"
						? await trackPromise(
								api.openEnrollment.getVEBOpenEnrollmentsForEachEmployee(getOpenEnrollmentListPayload)
						  )
						: null;

				// console.log("Response while switching the data", response);

				if (response) assignOpenEnrollments(response);
			}
		},
		[assignOpenEnrollments, employee, queryGroupNumber]
	);

	const handlePlanSwitchClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		// console.log("status of the switch component :", checked);
		setPlanCheckedStatus(checked);
	};

	const handleChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
		setTypeState(event.target.value as string);
	};
	// console.log("planCheckedStatus =>", planCheckedStatus);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.open_enrollment_lists);
		planCheckedStatus !== false ? getOpenEnrollmentLists("VEB") : getOpenEnrollmentLists("CORE");
	}, [planCheckedStatus, getOpenEnrollmentLists, setDashboardHeader]);

	return (
		<div className="open-enrollments-details-container">
			{/* <div className="tag-container">
				<div className="group-tag-indicator-right">Group Open Enrollments</div>
			</div> */}
			<div
				className="switch-container"
				id="switch-container"
				// style={{ display: "flex", justifyContent: "flex-end" }}
			>
				<FormControl variant="outlined">
					<Select
						labelId="demo-simple-select-standard-label"
						id="demo-simple-select-standard"
						value={typeState}
						onChange={handleChange}
						label="Age"
						startAdornment={
							<InputLabel id="demo-simple-select-standard-label">
								<SwapHorizIcon />
							</InputLabel>
						}
					>
						{ENROLLER_TYPE_SWITCHER.map((enrollerType: IOption, index: number) => (
							<MenuItem value={enrollerType.value} key={index}>
								{enrollerType.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</div>
			{typeState === ENROLLER_TYPE_SWITCHER[0].value ? null : (
				<div className="switch-container" id="switch-container">
					<Grid className="switch-grid-container" container spacing={1}>
						<Grid className="core-plan-switch-container" item>
							Core Plans
						</Grid>
						<Grid item>
							<GreenSwitch
								checked={planCheckedStatus}
								onChange={handlePlanSwitchClick}
								name="plan-switch"
							/>
						</Grid>
						<Grid className="veb-plan-switch-container" item>
							VEB Plans
						</Grid>
					</Grid>
				</div>
			)}
			{typeState === ENROLLER_TYPE_SWITCHER[0].value ? (
				<LazyEnrollments employee={employee} />
			) : (
				<>
					<div className="pf-title">Current Open Enrollments</div>
					<OpenEnrollmentListsGrid openEnrollments={currentOpenEnrollmentLists} />
					<div className="pf-title">Future Open Enrollments</div>
					<OpenEnrollmentListsGrid openEnrollments={futureOpenEnrollmentLists} />{" "}
				</>
			)}
		</div>
	);
};

export default OpenEnrollmentLists;
